(() => {
  'use strict';

  const TRIGGER_SELECTOR = '[data-pickup-drawer-trigger]';
  const DRAWER_SELECTOR = '[data-pickup-drawer]';
  const CLOSE_SELECTOR = '[data-pickup-drawer-close]';
  const OVERLAY_SELECTOR = '[data-pickup-drawer-overlay]';

  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';
  const HEADER_LOCK_CLASS = 'theme-pickup-drawer-open';
  const CLOSE_DURATION = 320;

  const getScrollbarCompensation = () =>
    Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const acquireScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      const compensation = `${getScrollbarCompensation()}px`;
      root.style.setProperty('--theme-scrollbar-compensation', compensation);
      if (body) body.style.setProperty('--theme-scrollbar-compensation', compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      if (body) body.classList.add(GLOBAL_LOCK_CLASS);
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
  };

  const releaseScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      if (body) body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty('--theme-scrollbar-compensation');
      if (body) body.style.removeProperty('--theme-scrollbar-compensation');
      return;
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
  };

  class PickupDrawer {
    constructor(container) {
      this.container = container;
      this.drawer = container.querySelector(DRAWER_SELECTOR);
      this.drawerOriginalParent = this.drawer ? this.drawer.parentNode : null;
      this.isOpen = false;
      this.closeTimer = null;
      this.openerEl = null;

      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
    }

    init() {
      if (!this.drawer) return;

      this.container.querySelectorAll(TRIGGER_SELECTOR).forEach((btn) => {
        btn.addEventListener('click', this._onTriggerClick);
      });

      this.drawer.querySelectorAll(CLOSE_SELECTOR).forEach((btn) => {
        btn.addEventListener('click', this._onCloseClick);
      });

      const overlay = this.drawer.querySelector(OVERLAY_SELECTOR);
      if (overlay) overlay.addEventListener('click', this._onOverlayClick);
    }

    open(opener) {
      if (this.isOpen || !this.drawer) return;

      this.openerEl = opener || null;
      clearTimeout(this.closeTimer);

      if (this.drawer.parentNode !== document.body) {
        document.body.appendChild(this.drawer);
      }

      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.classList.remove('is-closing');
      this.drawer.classList.add('is-opening');

      acquireScrollLock();
      document.documentElement.classList.add(HEADER_LOCK_CLASS);
      this.isOpen = true;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.drawer) return;
          this.drawer.classList.remove('is-opening');
          this.drawer.classList.add('is-open');
        });
      });

      document.addEventListener('keydown', this._onKeydown);

      const closeBtn = this.drawer.querySelector(CLOSE_SELECTOR);
      if (closeBtn) {
        requestAnimationFrame(() => closeBtn.focus({ preventScroll: true }));
      }
    }

    close() {
      if (!this.isOpen || !this.drawer) return;

      clearTimeout(this.closeTimer);
      this.isOpen = false;

      this.drawer.classList.add('is-closing');
      this.drawer.classList.remove('is-opening', 'is-open');
      this.drawer.setAttribute('aria-hidden', 'true');

      releaseScrollLock();
      document.documentElement.classList.remove(HEADER_LOCK_CLASS);
      document.removeEventListener('keydown', this._onKeydown);

      this.closeTimer = setTimeout(() => {
        if (!this.drawer) return;
        this.drawer.classList.remove('is-closing');
        this.drawer.hidden = true;
        if (this.drawerOriginalParent && this.drawer.parentNode === document.body) {
          this.drawerOriginalParent.appendChild(this.drawer);
        }
      }, CLOSE_DURATION);

      if (this.openerEl) {
        this.openerEl.focus({ preventScroll: true });
        this.openerEl = null;
      }
    }

    _onTriggerClick(e) {
      e.preventDefault();
      this.open(e.currentTarget);
    }

    _onCloseClick(e) {
      e.preventDefault();
      this.close();
    }

    _onOverlayClick() {
      this.close();
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
      }
    }

    destroy() {
      clearTimeout(this.closeTimer);
      if (this.isOpen && this.drawer) {
        this.isOpen = false;
        releaseScrollLock();
        document.documentElement.classList.remove(HEADER_LOCK_CLASS);
      }
      document.removeEventListener('keydown', this._onKeydown);
      if (this.drawer) {
        this.drawer.classList.remove('is-open', 'is-opening', 'is-closing');
        this.drawer.setAttribute('aria-hidden', 'true');
        this.drawer.hidden = true;
        if (this.drawerOriginalParent && this.drawer.parentNode === document.body) {
          this.drawerOriginalParent.appendChild(this.drawer);
        }
      }
    }
  }

  const PICKUP_SELECTOR = '[data-pickup-availability]';
  const instances = new Map();
  const pickupAbort = new WeakMap();

  const initPickup = (container) => {
    if (instances.has(container)) return;
    const ctrl = new PickupDrawer(container);
    ctrl.init();
    instances.set(container, ctrl);
  };

  // Re-render pickup for the newly selected variant via the Section Rendering
  // API, so the status + locations always reflect the current variant's actual
  // per-location stock (Dawn parity). Without this the panel stays on the
  // first-rendered variant's availability.
  const refreshPickup = async (container, variantId) => {
    const productUrl = container.dataset.productUrl;
    if (!productUrl || !variantId) return;

    pickupAbort.get(container)?.abort();
    const controller = new AbortController();
    pickupAbort.set(container, controller);

    const url = `${productUrl}?variant=${encodeURIComponent(variantId)}&section_id=pickup-availability`;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector(PICKUP_SELECTOR);

      const existing = instances.get(container);
      if (existing) {
        existing.destroy();
        instances.delete(container);
      }

      if (!fresh) {
        // No pickup availability for this variant — clear the panel.
        container.replaceChildren();
        container.removeAttribute('data-variant-id');
        return;
      }

      container.innerHTML = fresh.innerHTML;
      container.dataset.variantId = fresh.dataset.variantId || '';
      initPickup(container);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Pickup availability refresh error:', error);
      }
    }
  };

  document.addEventListener('variant:changed', (event) => {
    const variant = event?.detail?.variant;
    if (!variant) return;
    const eventSectionId = event.detail.sectionId;

    document.querySelectorAll(PICKUP_SELECTOR).forEach((container) => {
      const containerSectionId = container.dataset.sectionId;
      if (eventSectionId && containerSectionId && String(containerSectionId) !== String(eventSectionId)) {
        return;
      }
      refreshPickup(container, variant.id);
    });
  });

  document.querySelectorAll(PICKUP_SELECTOR).forEach(initPickup);

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target) {
      e.target.querySelectorAll('[data-pickup-availability]').forEach(initPickup);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    if (!e.target) return;
    e.target.querySelectorAll('[data-pickup-availability]').forEach((container) => {
      const ctrl = instances.get(container);
      if (ctrl) {
        ctrl.destroy();
        instances.delete(container);
      }
    });
  });
})();
